import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { Check, Loader2, Plus, X } from 'lucide-react';
import { apiErrorMessage, decideContractHolders } from '../../api/abfApi';
import type { FundChoice, HolderChoice } from '../../types/activationReadiness';
import { Button, FieldLabel, INK, Input, Row, fieldBase } from './reviewStyles';

/**
 * The booking funds of a facility and their commitment shares, chosen in contract
 * review (F-MK-001, plan decision D-1). One fund at 100% is the ordinary case; a
 * co-held facility lists every fund, and the shares must sum to 100. Activation
 * writes the set to the holders table, so the commitment and the closing advance
 * book per fund from the acquisition date.
 */

const Form = styled.form`
  display: grid;
  gap: 0.55rem;
`;

const Line = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 6.5rem auto;
  gap: 0.5rem;
  align-items: end;
  @media (max-width: 520px) { grid-template-columns: 1fr; }
`;

const Select = styled.select`
  ${fieldBase}
  option { background: #0f172a; color: ${INK.body}; }
`;

const Hint = styled.div`
  color: ${INK.faint};
  font-size: 0.66rem;
  line-height: 1.45;
`;

const Total = styled.div<{ $ok: boolean }>`
  color: ${p => p.$ok ? '#6ee7b7' : '#fcd34d'};
  font-size: 0.68rem;
  font-variant-numeric: tabular-nums;
`;

const ErrorLine = styled.div`
  color: #fca5a5;
  font-size: 0.7rem;
  line-height: 1.45;
`;

export interface HolderDraft {
  portfolioId: string;
  sharePct: string;
}

export const holderDrafts = (holders?: HolderChoice[] | null, fallbackPortfolioId?: number | null): HolderDraft[] => {
  if (holders && holders.length > 0) {
    return holders.map(holder => ({ portfolioId: String(holder.portfolioId), sharePct: trimShare(holder.sharePct) }));
  }
  return [{ portfolioId: fallbackPortfolioId ? String(fallbackPortfolioId) : '', sharePct: '100' }];
};

const trimShare = (value: number | string | null | undefined) => {
  if (value == null || value === '') return '';
  const n = Number(value);
  return Number.isFinite(n) ? String(Number(n.toFixed(6))) : String(value);
};

export const describeHolders = (fund: Pick<FundChoice, 'holders' | 'portfolioName' | 'portfolioCode'> | null | undefined) => {
  if (!fund) return null;
  if (fund.holders && fund.holders.length > 1) {
    return fund.holders
      .map(holder => `${holder.portfolioCode || holder.portfolioName || holder.portfolioId} ${trimShare(holder.sharePct)}%`)
      .join(' · ');
  }
  return fund.portfolioName ? `${fund.portfolioName}${fund.portfolioCode ? ` (${fund.portfolioCode})` : ''}` : null;
};

export interface HoldersFormProps {
  versionId: number;
  options: { id: number; portfolio_code: string; portfolio_name: string }[];
  initial: HolderDraft[];
  idPrefix: string;
  submitLabel: string;
  busyExternally?: boolean;
  onSaved: (message: string) => Promise<void> | void;
  onCancel?: () => void;
}

const HoldersForm: React.FC<HoldersFormProps> = ({
  versionId, options, initial, idPrefix, submitLabel, busyExternally, onSaved, onCancel,
}) => {
  const [lines, setLines] = useState<HolderDraft[]>(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const total = useMemo(() => lines.reduce((sum, line) => sum + (Number(line.sharePct) || 0), 0), [lines]);
  const totalOk = Math.abs(total - 100) < 0.000001;
  const complete = lines.every(line => line.portfolioId && Number(line.sharePct) > 0);
  const duplicate = new Set(lines.map(line => line.portfolioId)).size !== lines.length;
  const canSubmit = complete && totalOk && !duplicate && !saving && !busyExternally;

  const update = (index: number, patch: Partial<HolderDraft>) =>
    setLines(previous => previous.map((line, i) => (i === index ? { ...line, ...patch } : line)));

  const addFund = () => {
    setLines(previous => {
      const remaining = Math.max(0, 100 - previous.reduce((sum, line) => sum + (Number(line.sharePct) || 0), 0));
      return [...previous, { portfolioId: '', sharePct: remaining > 0 ? String(Number(remaining.toFixed(6))) : '' }];
    });
  };

  const removeFund = (index: number) => setLines(previous => previous.filter((_, i) => i !== index));

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSubmit) return;
    setSaving(true);
    setError(null);
    try {
      const holders = lines.map(line => ({ portfolioId: Number(line.portfolioId), sharePct: Number(line.sharePct) }));
      await decideContractHolders(versionId, holders);
      const names = lines.map(line => {
        const option = options.find(entry => String(entry.id) === line.portfolioId);
        const name = option ? `${option.portfolio_name} (${option.portfolio_code})` : 'The fund';
        return lines.length > 1 ? `${name} ${trimShare(line.sharePct)}%` : name;
      });
      await onSaved(lines.length > 1
        ? `${names.join(', ')} will hold this facility. The shares book from the acquisition date.`
        : `${names[0]} will hold this facility.`);
    } catch (failure) {
      setError(apiErrorMessage(failure, 'The booking funds were not saved.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Form onSubmit={submit}>
      {lines.map((line, index) => (
        <Line key={index}>
          <FieldLabel htmlFor={`${idPrefix}-fund-${index}`}>
            {index === 0 ? 'Fund' : `Co-holder ${index + 1}`}
            <Select id={`${idPrefix}-fund-${index}`} value={line.portfolioId} onChange={event => update(index, { portfolioId: event.target.value })}>
              <option value="">Choose the fund…</option>
              {options.map(option => (
                <option key={option.id} value={option.id}>{option.portfolio_name} ({option.portfolio_code})</option>
              ))}
            </Select>
          </FieldLabel>
          <FieldLabel htmlFor={`${idPrefix}-share-${index}`}>
            Share %
            <Input id={`${idPrefix}-share-${index}`} inputMode="decimal" value={line.sharePct}
              onChange={event => update(index, { sharePct: event.target.value })} placeholder="100" />
          </FieldLabel>
          {lines.length > 1 ? (
            <Button type="button" $variant="quiet" onClick={() => removeFund(index)} aria-label={`Remove fund ${index + 1}`}><X size={13} /></Button>
          ) : <span />}
        </Line>
      ))}
      <Row>
        <Button type="button" onClick={addFund} disabled={saving}><Plus size={13} /> Add a co-holder fund</Button>
        <Total $ok={totalOk}>{lines.length > 1 || !totalOk ? `Shares total ${Number(total.toFixed(6))}%${totalOk ? '' : ' · must be 100%'}` : ''}</Total>
      </Row>
      {duplicate && <ErrorLine role="alert">A fund is listed twice.</ErrorLine>}
      <Hint>The agreement does not decide this. Each fund's share of the commitment is fixed from the acquisition date; a later change is an amendment of the holders. Your choice is recorded under your name and the checker sees it before activating.</Hint>
      {error && <ErrorLine role="alert">{error}</ErrorLine>}
      <Row>
        <Button type="submit" $variant="primary" disabled={!canSubmit}>
          {saving ? <Loader2 size={13} className="spin" /> : <Check size={13} />} {submitLabel}
        </Button>
        {onCancel && <Button type="button" onClick={onCancel} disabled={saving}>Cancel</Button>}
      </Row>
    </Form>
  );
};

export default HoldersForm;
